/**
 * ArticleDetailModal Component
 * Modal showing full article: image, title, description, full content, authors, date, and optional "Open link" button.
 */

import React, { useState, useCallback, useMemo } from 'react'
import { Box, Typography } from '@mui/material'
import DOMPurify from 'dompurify'
import { useTranslation } from '../../shared/hooks/useIntractionTranslate'
import { INTERAX_COLORS } from '../../constants/colors'
import { FONT_FAMILY_PRIMARY } from '../../constants/interaxColors'
import { DefaultModal } from '../../shared/components/DefaultModal'
import { ExpertContentArticle } from '../../../../pure-js/types/ExpertVideo'
import { draftJsContentToHtml, isDraftJsContent } from '../../../../pure-js/libs/draftJsToHtml'
import { LinkModal } from './LinkModal'
import ButtonPrimary from '../../interaxDesignSystemComponents/ButtonPrimary'

export interface ArticleDetailModalProps {
  isOpen: boolean
  onClose: () => void
  article: ExpertContentArticle | null
  className?: string
}

function formatArticleDate(isoDate: string): string {
  try {
    const date = new Date(isoDate)
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  } catch {
    return isoDate
  }
}

/**
 * ArticleDetailModal - Full article view with image, full text, authors, date, and Open link button
 */
export const ArticleDetailModal: React.FC<ArticleDetailModalProps> = ({ isOpen, onClose, article, className = '' }) => {
  const { t } = useTranslation()
  const [linkModalOpen, setLinkModalOpen] = useState(false)

  const openLinkModal = useCallback(() => setLinkModalOpen(true), [])
  const closeLinkModal = useCallback(() => setLinkModalOpen(false), [])

  const descriptionHtml = useMemo(() => {
    if (!article) return null
    if (article.descriptionDraft && isDraftJsContent(article.descriptionDraft)) {
      return DOMPurify.sanitize(draftJsContentToHtml(article.descriptionDraft))
    }
    return null
  }, [article])
  const contentHtml = useMemo(() => {
    if (!article) return null
    if (article.contentDraft && isDraftJsContent(article.contentDraft)) {
      return DOMPurify.sanitize(draftJsContentToHtml(article.contentDraft))
    }
    return null
  }, [article])

  if (!article) return null

  return (
    <>
      <DefaultModal
        isOpen={isOpen}
        onClose={onClose}
        className={className}
        showCloseButton={true}
        maxWidth="66vw"
        backgroundColor={INTERAX_COLORS.offBlack}
      >
        <Box
          sx={{
            maxHeight: '80vh',
            overflowY: 'auto',
            pl: 2,
            pr: 6,
            mr: 5,
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-thumb': { backgroundColor: INTERAX_COLORS.thunderGrey, borderRadius: 3 }
          }}
        >
          {article.imageUrl && (
            <Box
              component="img"
              src={article.imageUrl}
              alt={article.title}
              sx={{
                width: '100%',
                height: 'auto',
                maxHeight: '320px',
                objectFit: 'cover',
                borderRadius: '8px',
                border: `1px solid ${INTERAX_COLORS.thunderGrey}`,
                mb: 2
              }}
            />
          )}

          <Typography
            sx={{
              fontFamily: FONT_FAMILY_PRIMARY,
              fontWeight: 500,
              fontSize: { xs: '24px', md: '28px' },
              lineHeight: 1.3,
              color: INTERAX_COLORS.white,
              mb: 2
            }}
          >
            {article.title}
          </Typography>

          {(article.authors?.length ?? 0) > 0 && (
            <Typography
              sx={{
                fontFamily: FONT_FAMILY_PRIMARY,
                fontSize: '14px',
                lineHeight: '20px',
                color: INTERAX_COLORS.thunderGrey,
                mb: 1
              }}
            >
              {t('landing.sections.knowledgeHub.authors') || 'By'}: {(article.authors ?? []).join(', ')}
            </Typography>
          )}

          {article.dateAdded && (
            <Typography
              sx={{
                fontFamily: FONT_FAMILY_PRIMARY,
                fontSize: '14px',
                lineHeight: '20px',
                color: INTERAX_COLORS.thunderGrey,
                mb: 2
              }}
            >
              {t('landing.sections.knowledgeHub.dateAdded') || 'Date'}: {formatArticleDate(article.dateAdded)}
            </Typography>
          )}

          {(descriptionHtml || article.description) && (
            <Box
              sx={{
                mb: 2,
                fontFamily: FONT_FAMILY_PRIMARY,
                fontSize: '16px',
                lineHeight: '24px',
                color: INTERAX_COLORS.thunderGrey,
                '& p': { margin: '0 0 1em' },
                '& ul, & ol': { margin: '0.5em 0', pl: 2 },
                '& h1, & h2, & h3': { margin: '0.5em 0' }
              }}
            >
              {descriptionHtml ? (
                <Box component="div" dangerouslySetInnerHTML={{ __html: descriptionHtml }} />
              ) : (
                <Typography component="div" sx={{ whiteSpace: 'pre-wrap' }}>
                  {article.description}
                </Typography>
              )}
            </Box>
          )}

          {(contentHtml || article.content) && (
            <Box
              sx={{
                mb: 3,
                fontFamily: FONT_FAMILY_PRIMARY,
                fontSize: '15px',
                lineHeight: '24px',
                color: INTERAX_COLORS.white,
                '& p': { margin: '0 0 1em' },
                '& ul, & ol': { margin: '0.5em 0', pl: 2 },
                '& h1, & h2, & h3': { margin: '0.5em 0' }
              }}
            >
              {contentHtml ? (
                <Box component="div" dangerouslySetInnerHTML={{ __html: contentHtml }} />
              ) : (
                <Typography component="div" sx={{ whiteSpace: 'pre-wrap' }}>
                  {article.content}
                </Typography>
              )}
            </Box>
          )}

          {article.link && (
            <Box sx={{ mt: 2 }}>
              <ButtonPrimary onClick={openLinkModal} size="medium">
                {t('landing.sections.knowledgeHub.openLink') || 'Open link'}
              </ButtonPrimary>
            </Box>
          )}
        </Box>
      </DefaultModal>

      {article.link && (
        <LinkModal
          isOpen={linkModalOpen}
          onClose={closeLinkModal}
          url={article.link}
          title={article.title}
          description={article.description}
        />
      )}
    </>
  )
}
